// src/pages/notifications/Notifications.tsx
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Pagination,
  Avatar,
  
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Stack,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  Alert,
  Divider,
  Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CommentIcon from "@mui/icons-material/Comment";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "../../constants";

dayjs.extend(relativeTime);

interface Notification {
  id: number;
  title: string;
  body: string;
  entityId: number;
  entityType: string;
  userId: number;
  createdAt: string;
}

interface NotificationResponse {
  pagination?: { total: number };
  data: Notification[];
}

type Sort = "newest" | "oldest";
type Filter = "all" | "unread";

const LS_KEY = "notifications:preferences";

// Match your MyAppBar height
const APPBAR_HEIGHT = 80;
const GAP = 8;

const iconForType = (entityType?: string) => {
  switch ((entityType || "").toUpperCase()) {
    case "TASK":
      return <AssignmentIcon />;
    case "COMMENT":
      return <CommentIcon />;
    case "PROJECT":
      return <WorkspacesIcon />;
    case "SYSTEM":
      return <ErrorOutlineIcon />;
    default:
      return <NotificationsIcon />;
  }
};

const routeFor = (entityType: string, entityId: number) => {
  const t = (entityType || "").toUpperCase();
  if (t === "TASK") return `/task/${entityId}`;
  if (t === "PROJECT") return `/project/${entityId}`;
  return `/task/${entityId}`;
};

const groupLabel = (d: dayjs.Dayjs) => {
  if (d.isSame(dayjs(), "day")) return "Today";
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
  return d.format("MMM D, YYYY");
};

const Notifications: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // data
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unseenIds, setUnseenIds] = useState<Set<number>>(new Set());

  // ui state
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<Sort>("newest");
  const [filter, setFilter] = useState<Filter>("all");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });

  // sticky math (measure controls panel)
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Use a safe default height so first paint is correct
  const [controlsHeight, setControlsHeight] = useState<number>(56);

  // compute sticky offsets
  const controlsTop = APPBAR_HEIGHT + GAP;
  const subheaderTop = controlsTop + controlsHeight + GAP;

  // measure controls height synchronously on layout
 useLayoutEffect(() => {
  // SSR guard
  if (typeof window === "undefined") return;

  const node = controlsRef.current;
  if (!node) return;

  const update = () => setControlsHeight(Math.ceil(node.offsetHeight));

  // prefer native ResizeObserver when available
  if (typeof window.ResizeObserver !== "undefined") {
    const ro = new window.ResizeObserver(() => update());
    ro.observe(node);
    update();
    return () => ro.disconnect();
  }

  // fallback: window resize listener (typed safely)
  const onResize = () => update();
  window.addEventListener?.("resize", onResize);
  update();
  return () => window.removeEventListener?.("resize", onResize);
}, []);


  // restore prefs
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.limit) setLimit(parsed.limit);
        if (parsed.sort) setSort(parsed.sort);
        if (parsed.filter) setFilter(parsed.filter);
      }
    } catch {}
  }, []);

  // persist prefs
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ limit, sort, filter }));
  }, [limit, sort, filter]);

  const fetchUnseen = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const res = await axios.get<Notification[]>(
        `${baseUrl}/notifications/unseen`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnseenIds(new Set(res.data.map((n) => n.id)));
    } catch (e) {
      console.warn("Failed to load unseen notifications", e);
    }
  };

  const fetchNotifications = async (p = page, l = limit) => {
    setLoading(true);
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get<NotificationResponse>(
        `${baseUrl}/notifications?page=${p}&limit=${l}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = response.data?.data ?? [];
      setNotifications(list);
      const total = response.data?.pagination?.total ?? list.length;
      setTotalPages(Math.max(1, Math.ceil(total / l)));
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch notifications.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // initial + when page/limit changes
  useEffect(() => {
    fetchNotifications(page, limit);
    fetchUnseen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // keyboard: ⌘/Ctrl+K focus search, ⌘/Ctrl+R refresh
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (meta && e.key.toLowerCase() === "r") {
        e.preventDefault();
        void fetchNotifications(1, limit);
        void fetchUnseen();
        setPage(1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [limit]);

  // computed: filter + search + sort + group
  const filteredSorted = useMemo(() => {
    const lower = search.trim().toLowerCase();
    let list = notifications.filter((n) => {
      if (filter === "unread" && !unseenIds.has(n.id)) return false;
      if (!lower) return true;
      const hay = `${n.title} ${n.body} ${n.entityType}`.toLowerCase();
      return hay.includes(lower);
    });

    if (sort === "newest") {
      list = list.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else {
      list = list.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    }

    const groups: Record<string, Notification[]> = {};
    list.forEach((n) => {
      const d = dayjs(n.createdAt);
      const label = groupLabel(d);
      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    });
    return groups;
  }, [notifications, search, sort, filter, unseenIds]);

  const markSeen = async (id: number) => {
    const token = localStorage.getItem("accessToken");
    try {
      await axios.patch(
        `${baseUrl}/notifications/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnseenIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Failed to mark as read.",
        severity: "error",
      });
    }
  };

  const markUnreadLocal = (id: number) => {
    setUnseenIds((prev) => new Set(prev).add(id));
  };

  const markAllSeen = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const unseenRes = await axios.get<Notification[]>(
        `${baseUrl}/notifications/unseen`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const ids = unseenRes.data.map((n) => n.id);
      await Promise.all(
        ids.map((id) =>
          axios.patch(`${baseUrl}/notifications/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } })
        )
      );
      await fetchUnseen();
      setSnackbar({
        open: true,
        message: "All notifications marked as read.",
        severity: "success",
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Failed to mark all as read.",
        severity: "error",
      });
    }
  };

  const refresh = async () => {
    await fetchNotifications(page, limit);
    await fetchUnseen();
  };

  return (
    <Box
      sx={{
       
        p: 2 ,
        width: "100%",
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 960 }}>
        {/* Header / Controls */}
        <Paper
          ref={controlsRef}
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            position: "sticky",
            top: `${APPBAR_HEIGHT + GAP}px`,
            zIndex: 10,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" fontWeight={800} sx={{ flex: 1, minWidth: 160 }}>
              Notifications
            </Typography>

            <TextField
              inputRef={searchRef}
              size="small"
              placeholder="Search notifications…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: "100%", sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unread">
                  Unread
                  <Badge sx={{ ml: 1 }} color="error" badgeContent={[...unseenIds].length || 0} />
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={20}>20 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Refresh (Ctrl/⌘+R)">
              <span>
                <IconButton onClick={refresh} disabled={loading}>
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Mark all as read">
              <span>
                <IconButton onClick={markAllSeen} disabled={![...unseenIds].length}>
                  <DoneAllIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Content */}
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            // IMPORTANT: allow sticky subheaders to render fully,
            // otherwise the first/second item looks cut off.
            overflow: "visible",
            bgcolor: theme.palette.background.paper,
          }}
        >
          {loading ? (
            <Box sx={{ py: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                You’re all caught up 🎉
              </Typography>
              <Typography color="text.secondary">No notifications at the moment.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {Object.entries(filteredSorted).map(([label, items]) => (
                <Box key={label} component="li">
                  <ListSubheader
                    component="div"
                    sx={{
                      position: "sticky",
                      top: `${subheaderTop}px`,
                      zIndex: 5,
                      bgcolor: theme.palette.background.paper,
                      fontWeight: 800,
                      color: theme.palette.text.secondary,
                      px: 2,
                      py: 1,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {label}
                  </ListSubheader>

                  {items.map((n) => {
                    const unread = unseenIds.has(n.id);
                    const leftAccent = unread ? theme.palette.primary.main : theme.palette.divider;

                    return (
                      <ListItem
                        key={n.id}
                        onClick={async () => {
                          if (unread) await markSeen(n.id);
                          navigate(routeFor(n.entityType, n.entityId));
                        }}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.03)",
                          },
                          display: "grid",
                          gridTemplateColumns: "auto 1fr auto",
                          gap: 1.5,
                          alignItems: "flex-start",
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 3,
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                            background: leftAccent,
                          },
                        }}
                        secondaryAction={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Tooltip title={unread ? "Mark as read" : "Mark as unread"}>
                              <IconButton
                                edge="end"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (unread) await markSeen(n.id);
                                  else markUnreadLocal(n.id);
                                }}
                                size="small"
                              >
                                {unread ? (
                                  <MarkEmailReadIcon fontSize="small" />
                                ) : (
                                  <MarkEmailUnreadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            mt: 0.2,
                            bgcolor: unread ? theme.palette.primary.main : theme.palette.grey[500],
                            color: theme.palette.getContrastText(
                              unread ? theme.palette.primary.main : theme.palette.grey[500]
                            ),
                          }}
                        >
                          {iconForType(n.entityType)}
                        </Avatar>

                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                              <Typography fontWeight={unread ? 800 : 600} sx={{ mr: 1 }}>
                                {n.title}
                              </Typography>
                              <Chip size="small" variant="outlined" label={n.entityType || "SYSTEM"} />
                            </Stack>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {n.body}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                                title={new Date(n.createdAt).toLocaleString()}
                              >
                                {dayjs(n.createdAt).fromNow()}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </Box>
              ))}
            </List>
          )}
        </Paper>

        {/* Footer / Pagination */}
        {!loading && totalPages > 1 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="center" pb={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                shape="rounded"
              />
            </Box>
          </>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
